import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {FormattedMessage, useIntl} from 'react-intl';
import {ButtonVariant, GritxButton, IUserProfile} from '@wholesalechange/chatcomponent';

import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {firstAuthorizedRoute, RoutesUrl, TranslationKeys} from 'utils/constants/routeConstants';
import {IStore} from 'redux/interface';
import {updateUserProfileAction} from 'redux/auth/authAsyncActions';
import {IOnboardingProfileFields} from 'redux/auth/interfaces';

import TextBox from 'components/text-box';

import Logo from 'assets/image/header/logo-2.png';
import LibraryIcon from 'assets/image/menu-auth/lib.svg';
import ResourceIcon from 'assets/image/menu-auth/resources.png';

import './styles.scss';

enum OnboardingStep {
  Info = 'info',
  Complete = 'complete'
}

const SmileyIcon = () => <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
  <circle cx="9" cy="10" r="1.25" fill="#1B3A5C"/>
  <circle cx="15" cy="10" r="1.25" fill="#1B3A5C"/>
  <path d="M8 14c1 1.3 2.4 2 4 2s3-0.7 4-2" stroke="#1B3A5C" strokeWidth="1.5" strokeLinecap="round"/>
</svg>;

const CheckIcon = () => <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
  <path d="M6 12.5l4 4 8-9" stroke="#1B3A5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
</svg>;

export const Onboarding: React.FunctionComponent = () => {
  const {userProfile} = useSelector((state: IStore) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();

  const [step, setStep] = useState(OnboardingStep.Info);
  const [text, setText] = useState('');
  const [wasTextShared, setWasTextShared] = useState(false);

  useGetTranslations({
    translationKey: TranslationKeys.Onboarding
  });

  async function completeOnboarding(sharedText: string) {
    const trimmedText = sharedText.trim();

    // Awaited so the redirect gate (PrivateRoute/Home, which reads userProfile.onboarding
    // from redux) always sees the update before the user can navigate off this screen —
    // otherwise a click right after submit/skip could race the save and bounce back to step 1.
    await dispatch(updateUserProfileAction({
      ...userProfile,
      onboarding: true,
      onboardingText: trimmedText || undefined
    } as IUserProfile & IOnboardingProfileFields));
    setWasTextShared(Boolean(trimmedText));
    setStep(OnboardingStep.Complete);
  }

  function handleSubmit() {
    completeOnboarding(text);
  }

  function handleSkip() {
    completeOnboarding('');
  }

  const infoStep = () => <div className="onboarding__card">
    <div className="onboarding__icon">
      <SmileyIcon/>
    </div>
    <h1 className="onboarding__title">
      <FormattedMessage
        id={'gritx.onboarding.info.title'}
        defaultMessage={'Welcome to Mira!'}
      />
    </h1>
    <p className="onboarding__subtitle">
      <FormattedMessage
        id={'gritx.onboarding.info.subtitle'}
        defaultMessage={'Tell me what brings you here today. Mira will learn and grow with you over time.'}
      />
    </p>
    <TextBox
      placeholder={intl.formatMessage({
        id: 'gritx.onboarding.info.placeholder',
        defaultMessage: 'Share whatever feels right — a few words or a few sentences. There are no wrong answers.'
      })}
      onChange={setText}
    />
    <GritxButton
      title={intl.formatMessage({
        id: 'gritx.onboarding.info.submit',
        defaultMessage: 'Submit'
      })}
      variant={ButtonVariant.Primary}
      className="onboarding__button onboarding__button--block"
      onClick={handleSubmit}
    />
    <button
      type="button"
      className="onboarding__skip-link"
      onClick={handleSkip}
    >
      <FormattedMessage
        id={'gritx.onboarding.info.skip'}
        defaultMessage={'Skip for now'}
      />
    </button>
  </div>;

  const completeMessage = wasTextShared
    ? {id: 'gritx.onboarding.complete.thankYou', defaultMessage: 'Thank you for sharing!'}
    : {id: 'gritx.onboarding.complete.welcome', defaultMessage: 'Welcome to Mira!'};

  const completeStep = () => <div className="onboarding__card">
    <div className="onboarding__icon">
      <CheckIcon/>
    </div>
    <h1 className="onboarding__title">
      <FormattedMessage {...completeMessage}/>
    </h1>
    <p className="onboarding__subtitle">
      <FormattedMessage
        id={'gritx.onboarding.complete.subtitle'}
        defaultMessage={'Now you have access to a Library and Community Resources that can help.'}
      />
    </p>
    <div className="onboarding__cards">
      <button
        type="button"
        className="onboarding__resource-card"
        onClick={() => navigate(RoutesUrl.Library)}
      >
        <img className="onboarding__resource-icon" src={LibraryIcon} alt=""/>
        <span className="onboarding__resource-text">
          <span className="onboarding__resource-title">
            <FormattedMessage id={'gritx.common.navigation.Library'} defaultMessage={'Library'}/>
          </span>
          <span className="onboarding__resource-description">
            <FormattedMessage id={'gritx.onboarding.complete.libraryDescription'} defaultMessage={'Tools & practices'}/>
          </span>
        </span>
        <span className="onboarding__resource-chevron"/>
      </button>
      <button
        type="button"
        className="onboarding__resource-card"
        onClick={() => navigate(RoutesUrl.DmResource)}
      >
        <img className="onboarding__resource-icon" src={ResourceIcon} alt=""/>
        <span className="onboarding__resource-text">
          <span className="onboarding__resource-title">
            <FormattedMessage id={'gritx.common.navigation.Resource'} defaultMessage={'Community Resources'}/>
          </span>
          <span className="onboarding__resource-description">
            <FormattedMessage id={'gritx.onboarding.complete.resourceDescription'} defaultMessage={'Trusted support near you'}/>
          </span>
        </span>
        <span className="onboarding__resource-chevron"/>
      </button>
    </div>
    <GritxButton
      title={intl.formatMessage({
        id: 'gritx.onboarding.complete.explore',
        defaultMessage: 'Explore now'
      })}
      variant={ButtonVariant.Primary}
      className="onboarding__button onboarding__button--block"
      onClick={() => navigate(firstAuthorizedRoute)}
    />
  </div>;

  return <div className="onboarding">
    <img className="onboarding__logo" src={Logo} alt="Mira Now"/>
    {step === OnboardingStep.Info ? infoStep() : completeStep()}
  </div>;
};

export default Onboarding;
